import React, { useState } from 'react';

import { Box } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import ActionButton from 'components/action-button';
import Modal, { ModalContent, ModalHeading } from 'components/modal';
import useErrorModal from 'hooks/use-error-modal';
import { useLogger } from 'state/logger';
import { usePaymentContext } from 'state/payment';
import { useUIContext } from 'state/ui';
import { CARD_ALIAS_TRANSLATION_ID } from 'utils/gift-card/constants';

import GiftCardImage from '../gift-card-image';

import { CardBalanceText, CurrentBalanceDescription, CurrentBalanceText, ModalHr } from './styled';

const SuccessTransferGiftCardModal: React.FC<{
  onCardTransfered: () => void;
}> = ({ onCardTransfered }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
  });
  const { formatMessage } = useIntl();
  const logger = useLogger();
  const payment = usePaymentContext();
  const { formatCurrencyForUser } = useUIContext();
  const navigate = useNavigate();
  const { state } = useLocation();
  const transferCard = async () => {
    setIsLoading(true);
    try {
      onCardTransfered();
      //navigate(-2) to go back two routes to payment screen
      navigate(-2);
    } catch (error) {
      openErrorDialog({ message: formatMessage({ id: 'giftCardAddingError' }), error });
      logger.error({ error, message: 'Error transfer gift card' });
    } finally {
      setIsLoading(false);
    }
  };

  const currentBalance = payment.getPrepaidPaymentMethod()?.prepaid?.currentBalance;
  const transferCardBalance = (state as { transferCardBalance?: number })?.transferCardBalance;
  const cardNumber = (state as { cardNumber?: string })?.cardNumber;
  const formattedCurrecyBalance = formatCurrencyForUser(currentBalance);
  const formattedTransferCardBalance = formatCurrencyForUser(transferCardBalance);
  const formatCardNumber = cardNumber?.replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ');

  const cardAlias = formatMessage({ id: CARD_ALIAS_TRANSLATION_ID });
  const isTransferCardBalanceValid = transferCardBalance && transferCardBalance > 0;
  const { actionDetails, actionHandler, actionText } = isTransferCardBalanceValid
    ? {
        actionDetails: formatMessage(
          { id: 'wouldYouLikeToAddToPrepaidCard' },
          { amount: formattedTransferCardBalance, cardAlias }
        ),
        actionText: formatMessage({ id: 'yesTransfer' }, { amount: formattedTransferCardBalance }),
        actionHandler: transferCard,
      }
    : {
        actionDetails: formatMessage({ id: 'wouldYouLikeToAddADifferentCard' }, { cardAlias }),
        actionText: formatMessage({ id: 'yesAddNewCard' }, { cardAlias }),
        actionHandler: () => {
          //navigate(-1) go back to transfer-gift-card route
          navigate(-1);
        },
      };

  return (
    <Modal onDismiss={() => navigate(-2)}>
      <ModalContent>
        <GiftCardImage />
        <ModalHeading id="modal-heading">
          {formatMessage(
            { id: 'prepaidCardBalance' },
            { cardAlias: formatMessage({ id: CARD_ALIAS_TRANSLATION_ID }) }
          )}
        </ModalHeading>
        <CardBalanceText data-testid="card-balance">
          {formatMessage(
            {
              id: 'theCardBalanceIs',
            },
            {
              cardNumber: (
                <b>
                  {formatCardNumber}
                  <br />
                </b>
              ),
            }
          )}
        </CardBalanceText>
        <CurrentBalanceText data-testid="transfer-balance">
          {formattedTransferCardBalance}
        </CurrentBalanceText>
        <ModalHr />
        <CurrentBalanceDescription>
          {formatMessage({ id: 'yourBalanceIs' }, { currentBalance: formattedCurrecyBalance })}
        </CurrentBalanceDescription>
        <CardBalanceText>{actionDetails}</CardBalanceText>
        <Box margin="20px auto 0" maxWidth="400px" width="100%">
          <ActionButton
            fullWidth
            data-testid="confirm-now"
            onClick={actionHandler}
            isLoading={isLoading}
          >
            {actionText}
          </ActionButton>
        </Box>
      </ModalContent>
      <ErrorDialog />
    </Modal>
  );
};

export default SuccessTransferGiftCardModal;
